/**
 * Outil « Interpolation de Lagrange » (Boîte à outils, MPSI).
 *
 * Interpoler : 2 à 6 points qu'on déplace (abscisses entières distinctes,
 * ordonnées au pas de 0,5) ; le polynôme interpolateur P passe par eux, les
 * polynômes de la base L_i s'affichent à la demande (1 en x_i, 0 aux autres
 * abscisses) ; formes produit et développée EXACTES, P = Σ y_i L_i, degré.
 * Approcher : une fonction interpolée en n + 1 nœuds équirépartis — l'écart
 * grandit au bord pour 1/(1 + x²) (phénomène de Runge), nœuds de Tchebychev
 * pour aller plus loin. Défi : L_i(x_j), droite, L_i(a), trois points, degré.
 * Logique : `lagrange.ts`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { cn, sampleFunctionPath } from '@jeraisonne/ui';
import {
  Note,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Segmented,
  Stepper,
  ToggleChip,
} from '../../ui/controls';
import { makeFrame, snapPoint, type Pt } from '../../ui/frame';
import { formatNumber, formatSci, frenchSpaces } from '../../ui/format';
import { seededRng } from '../../ui/random';
import { DragHandle, FIGURE_BG_CLASS, PlotGrid } from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import {
  FUNCTIONS,
  QUIZ_KINDS,
  basis,
  basisProductText,
  combinationText,
  degree,
  fromDec,
  idx,
  interpolate,
  lagrangeAt,
  makeQuiz,
  maxError,
  nodes,
  numText,
  peval,
  pevalNum,
  polyText,
  type FnId,
  type Point,
  type Quiz,
} from './lagrange';
import { frac } from '../../ui/exact';

const SIZE = 320;
const VP = { xMin: -4.5, xMax: 4.5, yMin: -4.5, yMax: 4.5 };
const COLORS = [
  'var(--color-primary-500)',
  'var(--color-accent-600)',
  'var(--color-success-600)',
  'var(--color-primary-300)',
  'var(--color-error-500)',
  'var(--color-ink-500)',
];
const CURVE = 'var(--color-ink-700)';
const MAX_PTS = 6;
const n1 = (v: number) => numText(fromDec(v));

/* ---------- interpoler ---------- */

type K = 'p0' | 'p1' | 'p2' | 'p3' | 'p4' | 'p5';
const KEYS: K[] = ['p0', 'p1', 'p2', 'p3', 'p4', 'p5'];

function Interpolate() {
  const [pts, setPts] = useState<Point[]>([
    { x: -2, y: 2 },
    { x: 0, y: -1 },
    { x: 2, y: 1.5 },
  ]);
  const [shown, setShown] = useState<number[]>([]);
  const svgRef = useRef<SVGSVGElement>(null);
  const frame = useMemo(() => makeFrame(VP, SIZE), []);
  const xs = pts.map((p) => p.x);
  const P = useMemo(() => interpolate(pts), [pts]);
  const n = pts.length - 1;
  const deg = degree(P);
  const ids = KEYS.slice(0, pts.length);

  const drag = useDrag<K>({
    svgRef,
    frame,
    ids,
    pointOf: (k) => pts[KEYS.indexOf(k)]!,
    onDrag: (k, p: Pt) => {
      const i = KEYS.indexOf(k);
      const q = snapPoint(p, { xMin: -4, xMax: 4, yMin: -4, yMax: 4 }, 1);
      const y = Math.round(Math.min(4, Math.max(-4, p.y)) * 2) / 2;
      setPts((old) => {
        const o = old[i]!;
        // au clavier (pas de 0,5), une flèche ← → avance d'une abscisse entière
        const dx = p.x - o.x;
        const nx = Math.abs(Math.abs(dx) - 0.5) < 1e-9 ? o.x + Math.sign(dx) : q.x;
        const x = Math.min(4, Math.max(-4, nx));
        // abscisses distinctes : une abscisse déjà prise est refusée (on garde l'ancienne)
        const taken = old.some((m, j) => j !== i && m.x === x);
        return old.map((m, j) => (j === i ? { x: taken ? m.x : x, y } : m));
      });
    },
    keyStep: 0.5,
  });

  const addPoint = () => {
    const free = [0, 1, -1, 2, -2, 3, -3, 4, -4].find((x) => !xs.includes(x));
    if (free === undefined || pts.length >= MAX_PTS) return;
    // ajouté à la fin : les points déjà placés gardent leur nom et leur couleur
    setPts([...pts, { x: free, y: 0 }]);
    setShown([]);
  };
  const removePoint = () => {
    if (pts.length <= 2) return;
    setPts(pts.slice(0, -1));
    setShown(shown.filter((i) => i < pts.length - 1));
  };

  const pPath = sampleFunctionPath((x) => pevalNum(P, x), [VP.xMin, VP.xMax], frame);
  const lines: string[] = [
    `${n + 1} points d’abscisses distinctes : P est l’unique polynôme de degré ≤ ${n} qui passe par eux.`,
    ...pts.map((_, i) => {
      const prod = basisProductText(xs, i).replace(/-(?=\d)/g, '−');
      const dev = polyText(basis(xs, i));
      return `L${idx(i)} = ${prod === dev ? dev : `${prod} = ${dev}`}`;
    }),
    `P = ${combinationText(pts.map((p) => p.y))} = ${polyText(P)}`,
    deg === -Infinity
      ? 'Tous les points sont sur l’axe des abscisses : P est le polynôme nul.'
      : deg < n
        ? `deg P = ${deg} < ${n} : les points sont sur une courbe d’un polynôme de degré plus petit${deg === 1 ? ' (ils sont alignés)' : deg === 0 ? ' (même ordonnée)' : ''}.`
        : `deg P = ${n}.`,
    `Vérification : ${pts.map((p) => `P(${n1(p.x)}) = ${numText(peval(P, frac(p.x)))}`).join(', ')}.`,
  ];
  const label = `Polynôme interpolateur P = ${polyText(P)} passant par ${pts.map((p) => `(${n1(p.x)} ; ${n1(p.y)})`).join(', ')}.`;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={cn(SECONDARY_BUTTON, 'disabled:opacity-40')}
          onClick={addPoint}
          disabled={pts.length >= MAX_PTS}
        >
          Ajouter un point
        </button>
        <button
          type="button"
          className={cn(SECONDARY_BUTTON, 'disabled:opacity-40')}
          onClick={removePoint}
          disabled={pts.length <= 2}
        >
          Retirer le dernier
        </button>
      </div>
      <div
        role="group"
        aria-label="Polynômes de la base de Lagrange"
        className="flex flex-wrap gap-2"
      >
        {pts.map((_, i) => (
          <ToggleChip
            key={i}
            on={shown.includes(i)}
            onClick={() =>
              setShown(shown.includes(i) ? shown.filter((j) => j !== i) : [...shown, i])
            }
          >
            {`L${idx(i)}`}
          </ToggleChip>
        ))}
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="group"
        aria-label={frenchSpaces(label)}
        className={cn(
          'border-ink-100 mx-auto block w-full max-w-[360px] touch-none select-none rounded-2xl border',
          FIGURE_BG_CLASS,
        )}
        {...drag.svgProps}
      >
        <PlotGrid frame={frame} step={1} />
        {shown.map((i) => (
          <g key={i}>
            <path
              d={sampleFunctionPath((x) => pevalNum(basis(xs, i), x), [VP.xMin, VP.xMax], frame)}
              fill="none"
              stroke={COLORS[i]}
              strokeWidth={2}
              strokeDasharray="6 4"
            />
            {/* L_i vaut 1 en x_i et 0 aux autres abscisses */}
            {xs.map((x, j) => (
              <circle
                key={j}
                cx={frame.tx(x)}
                cy={frame.ty(i === j ? 1 : 0)}
                r={4}
                fill="var(--fig-bg, #fff)"
                stroke={COLORS[i]}
                strokeWidth={2}
              />
            ))}
          </g>
        ))}
        <path d={pPath} fill="none" stroke={CURVE} strokeWidth={3} />
        {pts.map((p, i) => (
          <DragHandle
            key={KEYS[i]}
            x={frame.tx(p.x)}
            y={frame.ty(p.y)}
            color={COLORS[i]!}
            label={`M${idx(i)}`}
            labelDx={p.x > 3 ? -18 : 18}
            name={`Point M${idx(i)} (${n1(p.x)} ; ${n1(p.y)})`}
            active={drag.active === KEYS[i]}
            keyProps={drag.keyProps(KEYS[i]!)}
          />
        ))}
      </svg>
      <p className="text-ink-700 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <span className="flex items-center gap-1.5">
          <span aria-hidden="true" className="bg-ink-700 inline-block h-1 w-5 rounded" />
          polynôme interpolateur P
        </span>
        <span className="flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="border-ink-500 inline-block w-5 border-t-2 border-dashed"
          />
          {frenchSpaces('base de Lagrange Lᵢ (pastilles)')}
        </span>
      </p>
      <p className="sr-only" aria-live="polite">
        {frenchSpaces(`P = ${polyText(P)}`)}
      </p>
      <Note live={false}>
        {lines.map((l) => (
          <span key={l} data-testid="lg-line" className="mb-1 block last:mb-0">
            {frenchSpaces(l)}
          </span>
        ))}
      </Note>
      <p className="text-ink-500 text-xs">
        {frenchSpaces(
          'Déplace les points (abscisses entières distinctes, ordonnées au pas de 0,5). Les pastilles L₀, L₁… montrent la base de Lagrange : Lᵢ vaut 1 en xᵢ et 0 aux autres abscisses.',
        )}
      </p>
    </div>
  );
}

/* ---------- approcher une fonction ---------- */

const AVP = { xMin: -4.5, xMax: 4.5, yMin: -2.5, yMax: 2.5 };

function Approximate() {
  const [fn, setFn] = useState<FnId>('runge');
  const [n, setN] = useState(8);
  const [cheb, setCheb] = useState(false);
  const frame = useMemo(() => makeFrame(AVP, SIZE), []);
  const f = FUNCTIONS[fn].f;
  const xs = useMemo(() => nodes(n, -4, 4, cheb), [n, cheb]);
  const ys = useMemo(() => xs.map(f), [xs, f]);
  const err = useMemo(() => maxError(f, xs, -4, 4), [f, xs]);
  // n pairs seulement (l'écart alterne entre n pair et impair) : la tendance se lit
  const trend = useMemo(
    () => [4, 8, 12].map((k) => maxError(f, nodes(k, -4, 4, cheb), -4, 4)),
    [f, cheb],
  );
  // très petit : en écriture scientifique (sinon « ≈ 0 »)
  const fmtErr = (e: number) => (e < 1e-3 ? formatSci(e, 1) : formatNumber(e, e < 0.01 ? 4 : 2));
  const grows = trend[2]! > trend[0]!;
  const say = `Pour n = 4, 8, 12 (n pairs) : ≈ ${trend.map(fmtErr).join(' ; ')} — l’écart ${grows ? 'grandit' : 'diminue'} quand n augmente.`;
  const fPath = sampleFunctionPath(f, [AVP.xMin, AVP.xMax], frame);
  const pPath = sampleFunctionPath((x) => lagrangeAt(xs, ys, x), [-4, 4], frame);
  const text = `${FUNCTIONS[fn].label}, ${n + 1} nœuds ${cheb ? 'de Tchebychev' : 'équirépartis'} sur [−4 ; 4] : écart maximal |f − P| ≈ ${fmtErr(err)}. ${say}`;

  return (
    <div className="space-y-3">
      <Segmented
        ariaLabel="Fonction interpolée"
        value={fn}
        options={[
          { value: 'runge', label: '1/(1 + x²)' },
          { value: 'sin', label: 'sin x' },
          { value: 'abs', label: '|x|/2' },
        ]}
        onChange={setFn}
      />
      <Stepper
        label="Degré n (n + 1 nœuds)"
        value={n}
        min={1}
        max={14}
        onChange={setN}
        format={(v) => `n = ${v}`}
      />
      <ToggleChip on={cheb} onClick={() => setCheb(!cheb)}>
        Nœuds de Tchebychev (pour aller plus loin)
      </ToggleChip>
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={frenchSpaces(text)}
        className={cn(
          'border-ink-100 mx-auto block w-full max-w-[360px] rounded-2xl border',
          FIGURE_BG_CLASS,
        )}
      >
        <PlotGrid frame={frame} step={1} />
        <path d={fPath} fill="none" stroke="var(--color-primary-500)" strokeWidth={3} />
        <path
          d={pPath}
          fill="none"
          stroke="var(--color-accent-600)"
          strokeWidth={2.5}
          strokeDasharray="7 4"
        />
        {xs.map((x, i) => (
          <circle
            key={i}
            cx={frame.tx(x)}
            cy={frame.ty(ys[i]!)}
            r={4}
            fill="var(--color-accent-600)"
          />
        ))}
      </svg>
      <p className="text-ink-700 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <span className="flex items-center gap-1.5">
          <span aria-hidden="true" className="bg-primary-500 inline-block h-1 w-5 rounded" />
          {FUNCTIONS[fn].label}
        </span>
        <span className="flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="border-accent-600 inline-block w-5 border-t-[2.5px] border-dashed"
          />
          polynôme interpolateur P
        </span>
      </p>
      <Note>
        <span data-testid="lg-error">{frenchSpaces(text)}</span>
      </Note>
      <p className="text-ink-500 text-xs">
        {frenchSpaces(
          `${
            cheb
              ? 'Les nœuds de Tchebychev, plus serrés près des bords, évitent les oscillations de Runge : l’écart diminue quand n augmente.'
              : fn === 'runge'
                ? 'Avec des nœuds équirépartis, P colle à f au centre mais oscille de plus en plus près des bords : c’est le phénomène de Runge. Interpoler en plus de points ne garantit pas une meilleure approximation.'
                : fn === 'abs'
                  ? 'Pour |x|/2 aussi, avec des nœuds équirépartis, P oscille de plus en plus près des bords quand n grandit.'
                  : 'Pour sin x, l’écart diminue très vite : ici, interpoler en plus de points améliore l’approximation.'
          } Valeurs approchées : l’écart est mesuré sur 2 001 points de [−4 ; 4].`,
        )}
      </p>
    </div>
  );
}

/* ---------- défi ---------- */

function Challenge({ seed }: { seed?: number }) {
  const rnd = useMemo(() => (seed === undefined ? Math.random : seededRng(seed)), [seed]);
  const rounds = QUIZ_KINDS.length;
  const [count, setCount] = useState(1);
  const [q, setQ] = useState<Quiz>(() => makeQuiz(QUIZ_KINDS[0]!, rnd));
  const [picked, setPicked] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const moved = useRef(false);
  const done = picked !== null && count >= rounds;
  const chosen = picked === null ? null : q.options[picked]!;
  const right = q.options.find((o) => o.correct)!;

  useEffect(() => {
    if (moved.current) titleRef.current?.focus();
    moved.current = false;
  }, [q]);

  const choose = (i: number) => {
    if (picked !== null) return;
    setPicked(i);
    if (q.options[i]!.correct) setScore(score + 1);
  };
  const next = () => {
    const c = done ? 1 : count + 1;
    if (done) setScore(0);
    setCount(c);
    moved.current = true;
    setQ(makeQuiz(QUIZ_KINDS[c - 1]!, rnd));
    setPicked(null);
  };

  return (
    <div className="space-y-3">
      <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
        {frenchSpaces(`Question ${count} / ${rounds} · ${score} juste${score > 1 ? 's' : ''}`)}
      </p>
      <p ref={titleRef} tabIndex={-1} className="text-ink-900 text-base font-semibold outline-none">
        {frenchSpaces(q.prompt)}
      </p>
      <div role="group" aria-label="Réponses" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {q.options.map((o, i) => (
          <button
            key={o.text}
            type="button"
            onClick={() => choose(i)}
            aria-pressed={picked === i}
            aria-disabled={picked !== null && picked !== i}
            className={cn(
              'min-h-11 rounded-xl border px-3 py-2 text-center text-base font-semibold tabular-nums transition-colors',
              picked === null && 'border-ink-200 text-ink-800 hover:bg-ink-50',
              picked !== null && o.correct && 'border-success-600 bg-success-50 text-ink-900',
              picked === i && !o.correct && 'border-error-500 bg-error-50 text-ink-900',
              picked !== null && picked !== i && !o.correct && 'border-ink-100 text-ink-500',
            )}
          >
            {frenchSpaces(o.text)}
          </button>
        ))}
      </div>
      <div aria-live="polite">
        {chosen && (
          <Note live={false} tone={chosen.correct ? 'success' : 'error'}>
            <span data-testid="lg-feedback">
              {frenchSpaces(
                chosen.correct
                  ? `Exact. ${q.explain}`
                  : `${chosen.hint ?? ''} La bonne réponse : ${right.text}. ${q.explain}`,
              )}
            </span>
          </Note>
        )}
      </div>
      {picked !== null && (
        <div className="flex flex-wrap items-center gap-3">
          {done && (
            <p className="text-ink-900 text-sm font-semibold">
              {frenchSpaces(`Score : ${score} / ${rounds}`)}
            </p>
          )}
          <button type="button" className={done ? SECONDARY_BUTTON : PRIMARY_BUTTON} onClick={next}>
            {done ? 'Recommencer' : 'Question suivante'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------- outil ---------- */

export function LagrangeTool({ seed }: { /** graine du défi (tests) */ seed?: number }) {
  const [mode, setMode] = useState<'interp' | 'approx' | 'quiz'>('interp');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Partie de l’outil"
        value={mode}
        options={[
          { value: 'interp', label: 'Interpoler' },
          { value: 'approx', label: 'Approcher' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'interp'}>
        <Interpolate />
      </div>
      <div hidden={mode !== 'approx'}>
        <Approximate />
      </div>
      <div hidden={mode !== 'quiz'}>
        <Challenge seed={seed} />
      </div>
    </div>
  );
}
